import { DateRangeValue, Filter } from './filterTypes';

// A dashboard whose tiles read "Total Sales 50M" with no period answers no
// question: 50M since when, and is that ahead or behind? Picking a period is
// what makes the comparison — and therefore the number — mean something, so the
// dashboard now opens on one instead of summing all of history.

export type PeriodId =
  | 'this_month'
  | 'last_month'
  | 'this_quarter'
  | 'this_year'
  | 'all_time';

// Year-to-date, not month-to-date. Half of a construction tenant's tiles are
// snapshots of the whole book (outstanding balance, overdue instalments), and a
// one-month window made them read as if the book itself were small. A year is
// the shortest window that does not mislead on those.
export const DEFAULT_PERIOD: PeriodId = 'this_year';

export const PERIOD_LABEL: Record<PeriodId, string> = {
  this_month: 'This month',
  last_month: 'Last month',
  this_quarter: 'This quarter',
  this_year: 'This year',
  all_time: 'All time'
};

export const PERIOD_ORDER: PeriodId[] = [
  'this_month',
  'last_month',
  'this_quarter',
  'this_year',
  'all_time'
];

const iso = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`;

/** null for 'all_time' — no range means no filter, the pre-period behaviour. */
export function periodRange(period: PeriodId, today = new Date()): DateRangeValue | null {
  const y = today.getFullYear();
  const m = today.getMonth();
  switch (period) {
    case 'this_month':
      return { from: iso(new Date(y, m, 1)), to: iso(today) };
    case 'last_month':
      return { from: iso(new Date(y, m - 1, 1)), to: iso(new Date(y, m, 0)) };
    case 'this_quarter': {
      const qStart = Math.floor(m / 3) * 3;
      return { from: iso(new Date(y, qStart, 1)), to: iso(today) };
    }
    case 'this_year':
      return { from: iso(new Date(y, 0, 1)), to: iso(today) };
    case 'all_time':
    default:
      return null;
  }
}

/**
 * Which pill the active filters actually represent — the period is READ from
 * the filters, never held alongside them.
 *
 * Held as its own state it went stale the moment the date was cleared from the
 * filter bar instead of the pill row: no date filter was applied any more, but
 * "This month" stayed lit and every explanation that quoted the period was then
 * describing a window that was not in force.
 *
 * null means the active range is a custom one that no pill stands for — the
 * caller must show the dates themselves rather than name a period.
 */
export function periodFromFilters(
  filters: Filter[],
  active: Record<string, unknown>
): PeriodId | null {
  const dateFilter = primaryDateFilter(filters);
  if (!dateFilter) return null;

  const val = active[dateFilter.id] as DateRangeValue | undefined;
  // No range in force is exactly what 'all_time' means.
  if (!val || typeof val !== 'object' || (!val.from && !val.to)) return 'all_time';

  for (const p of PERIOD_ORDER) {
    const r = periodRange(p);
    if (r && r.from === val.from && r.to === val.to) return p;
  }
  return null;
}

/**
 * The date filter a period should drive. Tenants expose several date columns
 * (TRX_DATE, PERIOD_END_DATE…); the one covering the most tables is the one
 * that makes every widget move together.
 */
export function primaryDateFilter(filters: Filter[]): Filter | null {
  const dates = filters.filter((f) => f.type === 'daterange');
  if (dates.length === 0) return null;
  return [...dates].sort(
    (a, b) => (b.tables?.length ?? 0) - (a.tables?.length ?? 0)
  )[0];
}
